import React, { useState, useEffect, useMemo } from "react";
import { Box, Backdrop, Button } from "@mui/material";
import { FaLongArrowAltDown, FaLongArrowAltUp } from "react-icons/fa";

import Tooltip from "../../../../components/Tooltip";
import { ReactComponent as FlagUS } from "../../../../assets/flags/US.svg";
import TableRow, { TTableRowProps } from "../../common/TableRow";
import CircularProgress from "../../../../components/CircularProgress";

import { useAppDispatch, useAppSelector } from "../../../../store/hooks";
import {
  selectAverageData,
  selectOffers,
  selectRankAndPrice,
} from "../../../../features/productAnalyzer/productAnalyzer.selector";
import {
  getAveragesThunk,
  // getRanks,
  getProductDetails,
  updateLookupThunk,
} from "../../../../features/productAnalyzer/productAnalyzer.thunk";
import {
  formatCurrency,
  formatNumber,
  isNumber,
  toNumber,
} from "../../../../helpers/formatter";
import { selectProductAnalyzerSettings } from "../../../../features/settings/settings.selector";
import {
  PriceRange,
  TAverageData,
  TLowestFBASet,
} from "../../../../features/productAnalyzer/productAnalyzer.types";
import { DURATION_OPTIONS } from "../../common/DurationFilter";
import { getBsrDisplay } from "../QuickInfoCard";

import ThumbUpAltSharpIcon from "@mui/icons-material/ThumbUpAltSharp";
import ThumbDownAltSharpIcon from "@mui/icons-material/ThumbDownAltSharp";

import { useStyles } from "./index.styles";
import moment from "moment";
// import { selectIsFreePlan } from "../../../../features/pricingPlans/pricingPlans.selector";

interface RankTableProps {
  duration: string;
  asin: string;
  lookupId?: string;
}

const RankTable: React.FC<RankTableProps> = ({ duration, asin, lookupId }) => {
  const classes = useStyles();
  const dispatch = useAppDispatch();
  const { ranks, loadingRanks, netBBPriceChange, categoryProductCount } =
    useAppSelector((state) => selectRankAndPrice(state, duration));
  // const isFreePlan = useAppSelector(selectIsFreePlan);
  const { loadingAverages, averages } = useAppSelector(selectAverageData);
  const { minBsr, maxBsr } = useAppSelector(selectProductAnalyzerSettings);
  const { offers } = useAppSelector(selectOffers);

  const [rows, setRows] = useState<TTableRowProps[]>([]);
  const [startTime, setStartTime] = useState(new Date());
  const [timeFromNow, setTimeFromNow] = useState<string>("");

  const bsr: number = isNumber(ranks?.bsr) ? Number(ranks?.bsr) : -1;
  const bsrPercent: number = Math.ceil((bsr / categoryProductCount) * 100);
  const durationOption = DURATION_OPTIONS.find(
    (option) => option.id === duration,
  );
  const bbAvgPrice =
    averages?.buyBox?.[`avg${durationOption?.label}` as keyof TAverageData];
  const newAvgPrice =
    averages?.new?.[`avg${durationOption?.label}` as keyof TAverageData];
  const lowestFBA =
    averages?.lowestFBA?.[`${durationOption?.label}` as keyof TLowestFBASet];
  const lowestFBM =
    averages?.lowestFBM?.[`${durationOption?.label}` as keyof TLowestFBASet];
  const fbaCount = useMemo(
    () => offers.filter((offer) => offer.fulfillment === "FBA").length,
    [offers],
  );
  const fbmCount = useMemo(
    () => offers.filter((offer) => offer.fulfillment === "FBM").length,
    [offers],
  );

  useEffect(() => {
    if (!ranks) return;

    const isCriteria = bsrPercent >= minBsr && bsrPercent <= maxBsr;

    const renderGroupValues = (values?: PriceRange, count?: number) => {
      if (!values || values.average === null) return "-";

      return (
        <Tooltip
          title={
            duration === "Current" ? null : (
              <>
                Average: {formatCurrency(values.average)}
                <br />
                Minimum: {formatCurrency(values.minimum)}
                <br />
                Maximum: {formatCurrency(values.maximum)}
              </>
            )
          }
        >
          <Box
            className={`${classes.clickable} ${classes.textUnderline}`}
            onClick={() => {
              dispatch(
                updateLookupThunk({ salePrice: toNumber(values.average) }),
              );
            }}
          >
            {formatCurrency(values.average)} {count ? `(${count})` : ""}
          </Box>
        </Tooltip>
      );
    };

    setRows([
      {
        label: (
          <>
            <FlagUS />
            &nbsp;BSR (Top %)
          </>
        ),
        value: (
          <>
            {bsr > 0 ? (
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: "4px",
                }}
              >
                {bsr !== undefined || bsr !== null ? (
                  <>
                    {getBsrDisplay(bsr)}{" "}
                    <Box sx={{ color: isCriteria ? "#047647" : "#B42419" }}>
                      ({Math.ceil(bsrPercent)}% )
                    </Box>
                    {isCriteria ? (
                      <ThumbUpAltSharpIcon
                        sx={{ color: "#047647", fontSize: "12px" }}
                      />
                    ) : (
                      <ThumbDownAltSharpIcon
                        sx={{ color: "#B42419", fontSize: "12px" }}
                      />
                    )}
                  </>
                ) : (
                  <>-</>
                )}
              </Box>
            ) : (
              <Tooltip
                title={
                  <>&#x26A0;&#xFE0F; We do not know BSR of this product.</>
                }
              >
                <Box>-</Box>
              </Tooltip>
            )}
          </>
        ),
      },
      {
        label: "Keepa BSR Drops",
        value: ranks.keepaBSRDrops ?? "-",
      },
      {
        label: "Buy Box",
        value: renderGroupValues(ranks.buyBox),
      },
      {
        label: "Amazon",
        value: renderGroupValues(ranks.amazon),
      },
      {
        label: `Lowest FBA ${
          duration === "Current" && lowestFBA?.average ? "(Sellers)" : ""
        }`,
        value: renderGroupValues(
          lowestFBA,
          duration === "Current" ? fbaCount : undefined,
        ),
      },
      {
        label: `Lowest FBM ${
          duration === "Current" && lowestFBM?.average ? "(Sellers)" : ""
        }`,
        value: renderGroupValues(
          lowestFBM,
          duration === "Current" ? fbmCount : undefined,
        ),
      },
      {
        label: "Buy Box Average Price",
        value: isNumber(bbAvgPrice) ? (
          <Box
            className={`${classes.clickable} ${classes.textUnderline}`}
            onClick={() => {
              dispatch(updateLookupThunk({ salePrice: bbAvgPrice }));
            }}
          >
            {formatCurrency(bbAvgPrice)}
          </Box>
        ) : (
          "-"
        ),
        loading: loadingAverages,
      },
      {
        label: "New Average Price",
        value: isNumber(newAvgPrice) ? (
          <Box
            className={`${classes.clickable} ${classes.textUnderline}`}
            onClick={() => {
              dispatch(updateLookupThunk({ salePrice: newAvgPrice }));
            }}
          >
            {formatCurrency(newAvgPrice)}
          </Box>
        ) : (
          "-"
        ),
        loading: loadingAverages,
      },
      {
        label: (
          <Tooltip
            title={
              <>
                Net count of Buy Box price increases or decreases in selected
                time period. <br />
                An excessive number of net price decreases can indicate a price
                war.
              </>
            }
          >
            <Box className={classes.textUnderline}>Net BB Price Changes</Box>
          </Tooltip>
        ),
        value: (
          <Tooltip
            title={
              <>
                Buy Box price changes in selected time period
                <br /> Increases:
                {ranks.netBuyBoxPriceChanges?.increased}
                <br />
                Decreases:
                {ranks.netBuyBoxPriceChanges?.decreased}
              </>
            }
          >
            <Box className={classes.textUnderline}>
              {netBBPriceChange > 0 ? (
                <FaLongArrowAltUp className="down-up" color="#047647" />
              ) : (
                <FaLongArrowAltDown className="down-error" color="#B42419" />
              )}
              {Math.abs(netBBPriceChange)}&nbsp;
              {duration === "Current" && "last 30 days"}
            </Box>
          </Tooltip>
        ),
      },
      // {
      //   label: "Estimated Sales",
      //   value: `${formatNumber(ranks.estimatedSales, { forcePlusSuffix: true })}
      //       /mo`,
      //   // lock: isFreePlan,
      // },
      // {
      //   label: (
      //     <Tooltip
      //       title={
      //         <>
      //           Use with CAUTION this is an estimate and in Beta.
      //           <br />
      //           This is the estimated time to sell your first unit.
      //           <br />
      //           Based on your sale price, current stock levels and sales being
      //           shared between similar priced offers.
      //           <br />
      //           Prices and stock levels DO change.
      //         </>
      //       }
      //     >
      //       <Box className={classes.textUnderline}>Est. Time to Sale</Box>
      //     </Tooltip>
      //   ),
      //   value: (
      //     <Box>
      //       {ranks.estTimetoSale !== "Not enough data" &&
      //         ranks.estTimetoSale && (
      //           <FaLongArrowAltDown className="down-error" />
      //         )}

      //       <Box>{ranks?.estTimetoSale ?? "Not enough data"}</Box>
      //     </Box>
      //   ),
      // },
    ]);
  }, [
    ranks,
    setRows,
    averages,
    bsr,
    bsrPercent,
    classes.clickable,
    classes.textUnderline,
    dispatch,
    duration,
    loadingAverages,
    maxBsr,
    minBsr,
    netBBPriceChange,
    fbaCount,
    fbmCount,
  ]);

  const handleRefreshClick = () => {
    // dispatch(getRanks({ asin }))
    dispatch(getProductDetails({ asin }))
      .unwrap()
      .then(() => {
        setStartTime(new Date());
      });
    dispatch(getAveragesThunk(asin));
  };

  useEffect(() => {
    const timer = setInterval(() => {
      setTimeFromNow(moment(startTime).fromNow());
    }, 1000);

    return () => clearInterval(timer);
  }, [startTime]);

  if (!ranks) return null;

  return (
    <Box sx={{ width: "100%" }} className={classes.table}>
      <Box
        position="relative"
        sx={{
          borderBottom: "1px solid #181D1F26",
        }}
      >
        {rows.map(({ label, value, loading, lock }, index) => (
          <TableRow
            key={`ranks-and-prices-${index}`}
            label={label}
            value={value}
            loading={loading}
            lock={lock}
            asin={asin}
            lookupId={lookupId}
          />
        ))}

        {loadingRanks && (
          <Backdrop open={true} sx={{ position: "absolute", zIndex: 999 }}>
            <CircularProgress size="md" />
          </Backdrop>
        )}
      </Box>

      {/* <TableRow
        label={`Last checked ${timeFromNow.toLowerCase()}`}
        value={
          <Button
            variant="contained"
            size="small"
            onClick={handleRefreshClick}
            loading={loadingRanks}
          >
            Refresh
          </Button>
        }
      /> */}
    </Box>
  );
};

export default RankTable;
